import { Injectable } from '@angular/core';

import { AccountService } from './account.service';
import { CacheContext, HttpService } from './http.service';
import { PubSubService } from './pub-sub.service';

import { CraftFacilities } from '../models/craft.model';
import { Craft } from '@weavix/models/src/craft/craft';
import { Topic } from '@weavix/models/src/topic/topic';
import { Utils } from '../utils/utils';
import { colorPalette } from '@weavix/domain/src/utils/colors';


@Injectable({
    providedIn: 'root',
})
export class CraftService {

    constructor(
        private readonly httpService: HttpService,
        private readonly pubSubService: PubSubService,
        private readonly accountService: AccountService,
    ) { }

    static baseUrl = '/core/crafts';
    static cacheCollection = 'crafts';
    private readonly cacheContext: CacheContext = { collection: CraftService.cacheCollection, maxAge: 1_800_000 };

    static url = (id?: string) => id ? `${CraftService.baseUrl}/${id}` : CraftService.baseUrl;

    getAll(component: any, tags?: string[]): Promise<Craft[]> {
        return this.httpService.get<Craft[]>(component, CraftService.url(), { tags }, this.cacheContext);
    }

    getAllWithCorrespondingFacilities(component: any) {
        return this.httpService.get<CraftFacilities[]>(component, `${CraftService.url()}/facilities`, null);
    }

    getCraftFacilityIds(component: any, id: string) {
        return this.httpService.get<{ facilityIds: string[], globalAdmin: true }>(component, `${CraftService.url(id)}/facilities`, null);
    }

    async getEnabled(component: any) {
        const crafts = await this.getAll(component);
        return crafts.filter(x => !x.disabled);
    }

    async getEnabledMap(component: any) {
        return Utils.toObjectMap(await this.getEnabled(component), x => x.id, x => x);
    }

    get(component: any, id: string) {
        return this.httpService.get<Craft>(component, CraftService.url(id), null, this.cacheContext);
    }

    save(component: any, craft: Partial<Craft>) {
        return craft.id
            ? this.update(component, craft.id, craft)
            : this.add(component, craft);
    }

    private add(component: any, craft: Partial<Craft>) {
        return this.httpService.post<Craft>(component, CraftService.url(), craft, this.cacheContext);
    }

    private update(component: any, id: string, craft: Partial<Craft>) {
        return this.httpService.put<Craft>(component, CraftService.url(id), craft, this.cacheContext);
    }

    delete(component: any, id: string) {
        return this.httpService.delete<void>(component, CraftService.url(id), null, this.cacheContext);
    }

    colors() {
        return colorPalette;
    }

    async subscribeCraftUpdates(c: any, id: string = '+') {
        return await this.pubSubService.subscribe<Craft>(c, Topic.AccountCraftUpdated, [this.accountService.getAccountId(), id]);
    }

}
